import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { ApplicationSdk } from "..";
import { SEARCH_DELAY } from "../../constant";
import { formatName } from "../../helper";

/**
 * Load all orders and make it possible to manage them
 */
class OrderManagerStore {
    private sdk: ApplicationSdk;

    public orderListLoading: boolean = false;
    public orderList: Array<any> = [];
    public sortOrder: number = -1;
    public sortField: string = 'id';
    public searchValue: string = '';

    public refreshTimer: any = null;
    public timeout: any = null;

    public currentPage: number = 1;
    public maxPage: number = 1;
    public totalCount: number = 0;
    public latestPage: number = 1;

    public available_managers: Array<{ id: number; name: string }> = []; // List of available managers
    public filterManager: any = null;

    public available_cleaner: Array<{ id: number; name: string }> = []; // List of available cleaner
    public filterCleaner: any = null;

    public available_objects: Array<{ id: number; name: string }> = []; // List of available cleaner
    public filterObject: any = null;

    public filterStatus: string | null = null;

    constructor(sdk: ApplicationSdk) {
        makeObservable(this, {
            filterManager: observable,
            filterCleaner: observable,
            filterObject: observable,
            filterStatus: observable,
            available_managers: observable,
            available_cleaner: observable,
            available_objects: observable,
            sortOrder: observable,
            sortField: observable,
            orderListLoading: observable,
            orderList: observable,
            refreshTimer: observable,
            searchValue: observable,
            currentPage: observable,
            maxPage: observable,
            totalCount: observable,
            allOrders: computed,
            refresh: action,
            onPageChange: action,
            updateSearch: action,
            clearSearch: action,
            resetRefreshTimer: action,
            applyFilterManager: action,
            applyFilterCleaner: action,
            applyFilterObject: action,
            applyFilterStatus: action
        });

        this.sdk = sdk;

        // Load state from localStorage
        this.loadFromLocalStorage();
    }

    public async init(): Promise<void> {
        this.fetchOrderList();
    }

    public onPageChange(nextPage: number): void {
        this.fetchOrderList(nextPage);
    }

    public resetRefreshTimer(): void {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
    }

    public applyFilterManager(manager: any): void {
        this.filterManager = manager;
        this.saveToLocalStorage();
        this.fetchOrderList();
    }

    public applyFilterCleaner(cleaner: any): void {
        this.filterCleaner = cleaner;
        this.saveToLocalStorage();
        this.fetchOrderList();
    }

    public applyFilterObject(obj: any): void {
        this.filterObject = obj;
        this.saveToLocalStorage();
        this.fetchOrderList();
    }

    public applyFilterStatus(status: string): void {
        this.filterStatus = status;
        this.saveToLocalStorage();
        this.fetchOrderList();
    }

    public async fetchAvailableManagers(force: boolean = false): Promise<void> {
        if (!force) {
            if (this.available_managers.length > 1) {
                return;
            }
        }
        const managerResponse: any = await this.sdk.fetchApi('manager', 'query', {});

        runInAction(() => {
            if (managerResponse?.status === 200) {
                this.available_managers = managerResponse.json.data.map((manager: any) => ({
                    id: manager.id,
                    name: formatName(manager.firstname, manager.lastname)
                }));
            }
        });
    }

    public async fetchAvailableCleaner(force: boolean = false): Promise<void> {
        if (!force) {
            if (this.available_cleaner.length > 1) {
                return;
            }
        }
        const cleanerResponse: any = await this.sdk.fetchApi('cleaner', 'query', {
            format: 'simple',
            limit: 999
        });

        runInAction(() => {
            if (cleanerResponse?.status === 200) {
                this.available_cleaner = cleanerResponse.json.data.map((cleaner: any) => ({
                    id: cleaner.id,
                    name: formatName(cleaner.firstname, cleaner.lastname)
                }));
            }
        });
    }

    public async fetchAvailableObjects(force: boolean = false): Promise<void> {
        if (!force) {
            if (this.available_cleaner.length > 1) {
                return;
            }
        }
        const objResponse: any = await this.sdk.fetchApi('object', 'query', {
            format: 'simple',
            limit: 999
        });

        runInAction(() => {
            if (objResponse?.status === 200) {
                this.available_objects = objResponse.json.data.map((obj: any) => ({
                    id: obj.id,
                    name: obj.name
                }));
            }
        });
    }

    public async onSort(sortField: string): Promise<void> {
        runInAction(() => {
            if (this.sortField === sortField) {
                this.sortOrder = this.sortOrder === 1 ? -1 : 1;
            } else {
                this.sortOrder = -1;
            }
            this.sortField = sortField;

            // Save updated state to localStorage
            this.saveToLocalStorage();
        });

        await this.fetchOrderList();
    }

    public async onSearch(): Promise<void> {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
        await this.fetchOrderList();
    }

    public clearSearch(): void {
        if (this.searchValue) {
            this.searchValue = '';
            this.saveToLocalStorage();
            this.fetchOrderList();
        }
    }

    public updateSearch(value: string): void {
        this.searchValue = value;

        // Save updated state to localStorage
        this.saveToLocalStorage();

        if (this.timeout) {
            clearTimeout(this.timeout);
        }

        if (SEARCH_DELAY) {
            this.timeout = setTimeout(() => {
                this.fetchOrderList();
            }, SEARCH_DELAY);
        }
    }

    private saveToLocalStorage(): void {
        const state = {
            sortOrder: this.sortOrder,
            sortField: this.sortField,
            searchValue: this.searchValue,
            filterManager: this.filterManager,
            filterCleaner: this.filterCleaner,
            filterStatus: this.filterStatus
        };
        localStorage.setItem('order', JSON.stringify(state));
    }

    private loadFromLocalStorage(): void {
        const savedState = localStorage.getItem('order');
        if (savedState) {
            try {
                const { sortOrder, sortField, searchValue, filterManager, filterCleaner, filterStatus } = JSON.parse(savedState);
                runInAction(() => {
                    this.sortOrder = sortOrder || 1;
                    this.sortField = sortField || 'id';
                    this.searchValue = searchValue || '';
                    this.filterManager = filterManager || null;
                    this.filterCleaner = filterCleaner || null;
                    this.filterStatus = filterStatus || null;
                });
            } catch (error) {
                console.error('Failed to load state from localStorage:', error);
            }
        }
    }

    public refresh(): void {
        if (this.refreshTimer) {
            return;
        }
        const that = this;
        this.refreshTimer = setTimeout(() => that.resetRefreshTimer(), 5000);
        this.fetchOrderList();
    }

    public async onDialogLeave(dialog: string, event: any = null): Promise<void> {
        if (dialog === 'order' && event?.event === 'SAVED') {
            await this.fetchOrderList(this.latestPage);
        } else if (dialog === 'editorder' && event?.event === 'UPDATE') {
            await this.fetchOrderList(this.latestPage);
        } else if (dialog === 'editorder' && event?.event === 'NEW') {
            await this.fetchOrderList(this.latestPage);
        }
    }

    public async fetchOrderList(page: number = 1): Promise<void> {
        this.latestPage = page;
        runInAction(() => {
            this.orderListLoading = true;
        })
        const orderListResult: any = await this.sdk.fetchApi(
            'order',
            'query',
            {
                sortvalue: this.sortField,
                sort: this.sortOrder === 1 ? 'asc' : 'desc',
                search: this.searchValue || '',
                page,
                ...(this.filterManager?.id ? {
                    manager_id: this.filterManager?.id
                } : {}),
                ...(this.filterCleaner?.id ? {
                    cleaner_id: this.filterCleaner?.id
                } : {}),
                ...(this.filterStatus ? {
                    status: this.filterStatus
                } : {})
            });

        runInAction(() => {
            this.orderListLoading = false;

            if (orderListResult?.status === 200) {
                this.orderList = orderListResult.json.data.map((item: any) => {
                    return {
                        ...item,
                        cleaner: item?.cleaner_id === null ? '-' : formatName(item?.cleaner_firstname, item?.cleaner_lastname),
                    }
                });;

                this.currentPage = orderListResult.json.pagination.page;
                this.totalCount = orderListResult.json.pagination.total;
                this.maxPage = Math.ceil(this.totalCount / orderListResult.json.pagination.pageSize);
            }
        });
    }

    get allOrders(): Array<any> | null {
        if (!this.orderList) return null;
        return this.orderList.filter((item: any) => {
            return true;
            // return item.name.toLowerCase().includes(this.productQuickSearch.toLowerCase()) || item?.description?.toLowerCase()?.includes(this.productQuickSearch.toLowerCase());
        });
    }

    public onLeaveStore(): void {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }

        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }
    }
}

export default OrderManagerStore;